import { useState } from "react";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import BulkDropzone from "@/components/BulkDropzone";
import api, { formatApiErrorDetail } from "@/lib/api";
import { findBestMatch } from "@/lib/fuzzyMatch";
import { SERVICES } from "@/constants/testIds";

/** Carga masiva de fotos de servicios: arrastrá/seleccioná varios archivos,
 * el sistema los empareja por nombre con un servicio del catálogo (tolerante
 * a tildes/mayúsculas/guiones/errores de tipeo), se puede corregir cada fila
 * antes de confirmar. */
export default function BulkPhotoUploadModal({ open, onOpenChange, services, onDone }) {
  const [step, setStep] = useState("drop"); // drop | review | uploading | result
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);

  const reset = () => {
    rows.forEach((r) => URL.revokeObjectURL(r.previewUrl));
    setStep("drop"); setRows([]); setProgress({ done: 0, total: 0 }); setResult(null);
  };

  const handleClose = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const handleFiles = (files) => {
    const built = files.map((file, idx) => {
      const { match, score } = findBestMatch(file.name, services, (s) => s.name);
      return {
        key: `${idx}-${file.name}`,
        file,
        previewUrl: URL.createObjectURL(file),
        filename: file.name,
        serviceId: match ? match.id : "none",
        score,
      };
    });
    setRows(built);
    setStep("review");
  };

  const updateRow = (key, serviceId) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, serviceId } : r)));
  const removeRow = (key) => setRows((rs) => rs.filter((r) => r.key !== key));

  const serviceById = (id) => services.find((s) => s.id === id);
  const assignedCounts = rows.reduce((acc, r) => {
    if (r.serviceId !== "none") acc[r.serviceId] = (acc[r.serviceId] || 0) + 1;
    return acc;
  }, {});
  const duplicateServiceIds = new Set(Object.keys(assignedCounts).filter((id) => assignedCounts[id] > 1));
  const hasDuplicates = duplicateServiceIds.size > 0;
  const assignableCount = rows.filter((r) => r.serviceId !== "none").length;

  const handleConfirm = async () => {
    setStep("uploading");
    let success = 0;
    let skipped = 0;
    const errors = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      setProgress({ done: i, total: rows.length });
      if (row.serviceId === "none") { skipped++; continue; }
      try {
        const formData = new FormData();
        formData.append("file", row.file);
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
        await api.put(`/services/${row.serviceId}`, { photo_url: data.url });
        success++;
      } catch (err) {
        errors.push({ filename: row.filename, error: formatApiErrorDetail(err.response?.data?.detail) });
      }
    }
    setProgress({ done: rows.length, total: rows.length });
    setResult({ success, skipped, errors });
    setStep("result");
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid={SERVICES.bulkModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Subir fotos en lote</DialogTitle>
        </DialogHeader>

        {step === "drop" && (
          <div className="py-2">
            <BulkDropzone
              onFiles={handleFiles}
              accept="image/png,image/jpeg,image/jpg,image/webp,image/avif"
              label="Arrastrá o hacé click para elegir varias fotos"
              hint="Se emparejan automáticamente con el servicio por el nombre del archivo (jpg, png, webp, avif). Podés corregir cada asignación antes de confirmar."
              testId={SERVICES.bulkDropzone}
            />
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {rows.length} archivo(s) &middot; {assignableCount} con servicio asignado
            </p>
            {hasDuplicates && (
              <div className="flex items-start gap-2 bg-amber-50 text-amber-800 text-sm rounded-lg p-3" data-testid={SERVICES.bulkDuplicateWarning}>
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Hay más de un archivo apuntando al mismo servicio. Corregí la asignación antes de confirmar (cada servicio solo puede tener una foto).</span>
              </div>
            )}
            <div className="border border-border rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
              <Table data-testid={SERVICES.bulkTable}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16"></TableHead>
                    <TableHead>Archivo</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, idx) => {
                    const existing = r.serviceId !== "none" ? serviceById(r.serviceId) : null;
                    const willReplace = existing?.photo_url;
                    const isDuplicate = duplicateServiceIds.has(r.serviceId);
                    return (
                      <TableRow key={r.key} data-testid={`${SERVICES.bulkRowPrefix}${idx}`}>
                        <TableCell>
                          <img src={r.previewUrl} alt={r.filename} className="w-12 h-12 object-cover rounded border border-border" />
                        </TableCell>
                        <TableCell>
                          <p className="text-sm truncate max-w-[220px]">{r.filename}</p>
                          {r.serviceId === "none" && (
                            <span className="text-[11px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded" data-testid={`${SERVICES.bulkNoMatchBadgePrefix}${idx}`}>Sin coincidencia</span>
                          )}
                          {willReplace && (
                            <span className="text-[11px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded ml-1" data-testid={`${SERVICES.bulkReplaceBadgePrefix}${idx}`}>Va a reemplazar la foto actual</span>
                          )}
                          {isDuplicate && <span className="text-[11px] font-medium text-destructive block mt-0.5">Duplicado con otro archivo</span>}
                        </TableCell>
                        <TableCell>
                          <Select value={r.serviceId} onValueChange={(v) => updateRow(r.key, v)}>
                            <SelectTrigger className="w-56 h-8 text-xs" data-testid={`${SERVICES.bulkServiceSelectPrefix}${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sin asignar</SelectItem>
                              {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => removeRow(r.key)} data-testid={`${SERVICES.bulkRemoveButtonPrefix}${idx}`}>
                            <X className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {step === "uploading" && (
          <div className="py-10 space-y-3" data-testid={SERVICES.bulkProgress}>
            <p className="text-sm text-center text-muted-foreground">Subiendo {progress.done} de {progress.total}...</p>
            <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
          </div>
        )}

        {step === "result" && result && (
          <div className="py-6 text-center space-y-3" data-testid={SERVICES.bulkResultSummary}>
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <p className="text-lg font-serif text-foreground">Carga completa</p>
            <p className="text-sm text-muted-foreground">
              {result.success} foto(s) cargadas &middot; {result.skipped} sin asignar &middot; {result.errors.length} error(es)
            </p>
            {result.errors.length > 0 && (
              <ul className="text-xs text-destructive text-left max-w-sm mx-auto space-y-1">
                {result.errors.map((e, i) => <li key={i}>{e.filename}: {e.error}</li>)}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "drop" && (
            <Button variant="outline" onClick={() => handleClose(false)} data-testid={SERVICES.bulkCancelButton}>Cancelar</Button>
          )}
          {step === "review" && (
            <>
              <Button variant="outline" onClick={reset} data-testid={SERVICES.bulkBackButton}>Volver</Button>
              <Button onClick={handleConfirm} disabled={hasDuplicates || assignableCount === 0} data-testid={SERVICES.bulkConfirmButton}>
                Confirmar carga ({assignableCount})
              </Button>
            </>
          )}
          {step === "result" && (
            <Button onClick={() => handleClose(false)}>Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
